/* UPVCSTORE APECS keying options (sections/upvc-apecs-keying.liquid).
 * Enhances plain add-to-basket forms: quantity steppers, the keyed alike
 * cylinder planner, basket hints, the extra-key "For cylinder" property and
 * an in-page add. Without this script the forms still post to the cart.
 */
(() => {
  const MAX_QTY = 50;

  const clamp = (value, min, max) => {
    const number = parseInt(value, 10);
    if (Number.isNaN(number)) return min;
    return Math.min(Math.max(number, min), max);
  };

  const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

  const formatterFor = (section) => {
    const currency = section.dataset.currency || 'GBP';
    const locale = section.dataset.locale ? `${section.dataset.locale}-GB` : 'en-GB';
    let format;
    try {
      format = new Intl.NumberFormat(locale, { style: 'currency', currency });
    } catch (error) {
      format = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });
    }
    return (cents) => format.format(cents / 100);
  };

  const fetchCart = (section) =>
    fetch(`${section.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);

  const readVariants = (section) => {
    const node = section.querySelector('[data-keying-variants]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  };

  // Returns the full label for the line item property and the short label
  // (size / finish) shown beside the extra keys button.
  const currentCylinder = (variants, variantId) => {
    if (!variants) return null;
    const idField = document.querySelector('[data-product-wrapper] form[action*="/cart/add"] [name="id"]');
    const urlVariant = new URLSearchParams(window.location.search).get('variant');
    const id = variantId || (idField && idField.value) || urlVariant;
    const title = id && variants.variants[id];
    if (variants.single || !title) return { full: variants.product, short: variants.product };
    return { full: `${variants.product} — ${title}`, short: title };
  };

  const initCard = (section, card, money, variants) => {
    const form = card.querySelector('[data-keying-form]');
    if (!form) return null;

    const kind = card.dataset.keyingService;
    const price = parseInt(card.dataset.price, 10) || 0;
    const quantity = form.querySelector('[data-keying-quantity]');
    const qtyWrap = form.querySelector('[data-keying-qty]');
    const submit = form.querySelector('[data-keying-submit]');
    const buttonLabel = form.querySelector('[data-keying-button-label]');
    const baseLabel = buttonLabel ? buttonLabel.textContent.trim() : '';
    const status = form.querySelector('[data-keying-status]');
    const planner = form.querySelector('[data-keying-planner]');
    const cylinders = form.querySelector('[data-keying-cylinders]');
    const summary = form.querySelector('[data-keying-summary]');
    const basket = form.querySelector('[data-keying-basket]');
    const forField = form.querySelector('[data-keying-for]');
    const forLabel = form.querySelector('[data-keying-for-label]');
    const chargeEach = card.dataset.chargeRule === 'each';

    const refreshButton = () => {
      const qty = clamp(quantity.value, 1, MAX_QTY);
      if (buttonLabel && price) buttonLabel.textContent = `${baseLabel} · ${money(price * qty)}`;
    };

    const chargesFor = (count) => (chargeEach ? count : count - 1);
    let basketCharges = 0;

    // Adds only the charges still missing, so charges already in the basket
    // are not added twice.
    const refreshPlanner = () => {
      const count = clamp(cylinders.value, 2, MAX_QTY);
      const charges = Math.max(chargesFor(count), 1);
      const toAdd = charges - basketCharges;
      let text = `${plural(count, 'cylinder', 'cylinders')} on one key: ${plural(charges, 'keyed alike charge', 'keyed alike charges')}.`;
      if (!chargeEach) text += ' The first cylinder sets the key, so it needs no charge.';
      if (basketCharges && toAdd > 0) {
        text += ` ${plural(basketCharges, 'charge is', 'charges are')} already in your basket, so this adds ${toAdd}.`;
      } else if (basketCharges) {
        text += ' Your basket already has enough charges for these cylinders.';
      }
      quantity.value = Math.max(toAdd, 1);
      summary.textContent = text;
      refreshButton();
    };

    const refreshFor = (variantId) => {
      if (!forField) return;
      const cylinder = currentCylinder(variants, variantId);
      if (!cylinder) return;
      forField.value = cylinder.full;
      if (forLabel) forLabel.textContent = cylinder.short;
    };

    form.querySelectorAll('[data-keying-step]').forEach((button) => {
      button.hidden = false;
      button.addEventListener('click', () => {
        const field = button.parentElement.querySelector('input');
        const min = parseInt(field.min, 10) || 1;
        field.value = clamp(field.value, min, MAX_QTY) + parseInt(button.dataset.keyingStep, 10);
        field.value = clamp(field.value, min, MAX_QTY);
        field.dispatchEvent(new Event('input', { bubbles: true }));
      });
    });

    quantity.addEventListener('input', refreshButton);
    quantity.addEventListener('change', () => {
      quantity.value = clamp(quantity.value, 1, MAX_QTY);
      refreshButton();
    });

    if (kind === 'keyed_alike' && planner && cylinders) {
      planner.hidden = false;
      qtyWrap.hidden = true;
      cylinders.addEventListener('input', refreshPlanner);
      cylinders.addEventListener('change', () => {
        cylinders.value = clamp(cylinders.value, 2, MAX_QTY);
        refreshPlanner();
      });
      refreshPlanner();
    } else {
      refreshButton();
    }

    if (forField) {
      refreshFor();
      // Empire's <variant-selection> fires a non-bubbling variant-change event.
      const selection = document.querySelector('[data-product-wrapper] variant-selection');
      if (selection) {
        selection.addEventListener('variant-change', (event) => {
          const variant = event.detail && event.detail.variant;
          refreshFor(variant ? String(variant.id) : null);
        });
      }
      document.addEventListener('change', (event) => {
        if (event.target.closest('[data-product-wrapper]')) window.setTimeout(() => refreshFor(), 50);
      });
    }

    const updateBasket = (cart) => {
      if (!cart || kind !== 'keyed_alike' || !basket) return;
      const match = (card.dataset.basketMatch || '').trim();
      const variantId = parseInt(card.dataset.variantId, 10);
      let cylinderCount = 0;
      let chargeCount = 0;
      cart.items.forEach((item) => {
        if (item.variant_id === variantId) chargeCount += item.quantity;
        else if (match && item.handle && item.handle.indexOf(match) !== -1) cylinderCount += item.quantity;
      });
      basketCharges = chargeCount;
      if (cylinderCount >= 2 && !cylinders.dataset.touched) {
        cylinders.value = Math.min(cylinderCount, MAX_QTY);
      }
      refreshPlanner();
      if (!cylinderCount && !chargeCount) {
        basket.hidden = true;
        return;
      }
      const needed = cylinderCount >= 2 ? Math.max(chargesFor(cylinderCount), 0) : 0;
      const charges = plural(chargeCount, 'keyed alike charge', 'keyed alike charges');
      let text;
      if (!cylinderCount) {
        text = `In your basket: ${charges}, but no AP v3 cylinders yet.`;
      } else {
        text = `In your basket: ${plural(cylinderCount, 'AP v3 cylinder', 'AP v3 cylinders')}`;
        text += chargeCount ? ` and ${charges}.` : '.';
        if (needed && chargeCount >= needed) text += ' You have enough charges to key them all alike.';
      }
      basket.textContent = text;
      basket.hidden = false;
    };

    if (cylinders) {
      cylinders.addEventListener('input', () => {
        cylinders.dataset.touched = 'true';
      });
    }

    form.addEventListener('submit', (event) => {
      if (!window.fetch || !window.FormData) return;
      event.preventDefault();
      if (forField) refreshFor();
      const qty = clamp(quantity.value, 1, MAX_QTY);
      quantity.value = qty;
      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
      status.classList.remove('is-error');
      status.textContent = 'Adding to your basket…';

      fetch(`${section.dataset.cartAddUrl}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      })
        .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
        .then(({ ok, data }) => {
          if (!ok) throw new Error(data.description || data.message || 'This could not be added.');
          const title = (data.product_title || 'item').trim();
          status.innerHTML = '';
          status.append(`Added ${qty} × ${title}. `);
          const link = document.createElement('a');
          link.href = section.dataset.cartUrl;
          link.textContent = 'View basket';
          status.append(link);
          return fetchCart(section).then((cart) => {
            if (!cart) return;
            window.dispatchEvent(new CustomEvent('cartcount:update', { detail: cart }));
            section.dispatchEvent(new CustomEvent('upvc-keying:cart', { detail: cart }));
          });
        })
        .catch((error) => {
          status.classList.add('is-error');
          status.textContent = `${error.message} Please try again or message us.`;
        })
        .finally(() => {
          submit.disabled = false;
          submit.removeAttribute('aria-busy');
        });
    });

    return updateBasket;
  };

  const initSection = (section) => {
    if (section.dataset.keyingReady) return;
    section.dataset.keyingReady = 'true';
    const money = formatterFor(section);
    const variants = readVariants(section);
    const basketUpdaters = [];

    section.querySelectorAll('[data-keying-service]').forEach((card) => {
      const updater = initCard(section, card, money, variants);
      if (updater) basketUpdaters.push(updater);
    });

    if (!basketUpdaters.length) return;
    const applyCart = (cart) => basketUpdaters.forEach((update) => update(cart));
    section.addEventListener('upvc-keying:cart', (event) => applyCart(event.detail));

    // Cylinders may be added from the buy box above without a reload, so
    // re-read the basket whenever the section scrolls into view.
    let lastFetch = 0;
    const refresh = (retry = true) => {
      if (Date.now() - lastFetch < 4000) return;
      fetchCart(section).then((cart) => {
        if (cart) {
          lastFetch = Date.now();
          applyCart(cart);
        } else if (retry) {
          window.setTimeout(() => refresh(false), 1500);
        }
      });
    };
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) refresh();
      }).observe(section);
    } else {
      refresh();
    }
  };

  const init = (root = document) => root.querySelectorAll('[data-upvc-keying]').forEach(initSection);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
